/* Celdas combinadas de FiboSheets: lo mínimo para que un Excel con títulos
 * combinados («Resumen de octubre» sobre A1:F1) entre como estaba y vuelva a
 * salir igual.
 *
 * ── EL MODELO ──────────────────────────────────────────────────────────────
 *
 * Una hoja puede traer `combinadas: ["A1:C1", "A5:B6", …]`. El valor vive en
 * la celda de arriba a la izquierda (la «ancla»), como en Excel; las demás
 * quedan vacías y no se dibujan. Se guarda sólo si hay alguna.
 *
 * ── POR QUÉ NO HAY ROWSPAN ─────────────────────────────────────────────────
 *
 * La grilla está virtualizada: sólo se montan las filas a la vista, y la fila
 * 1 puede ir fija en el encabezado. Un `rowSpan` que empieza en una fila que
 * no se dibujó (o en el `<thead>`) corre todas las celdas de las filas de
 * abajo. Entonces cada fila dibuja SU tramo de la combinada como una sola
 * celda ancha (`colSpan`), y entre tramos de la misma combinada se borra la
 * línea horizontal: se ve como un bloque, ande o no la virtualización, haya o
 * no filtros. El texto va en el tramo de arriba (en Excel, por defecto, va
 * abajo; es lo único que cambia), salvo que la ancla esté centrada en vertical
 * (`valign: "middle"`, lo que deja «Combinar y centrar»): ahí la pantalla baja
 * el texto hasta la mitad del bloque, sin que el tramo deje de ser el de
 * arriba.
 *
 * ── COMBINAR Y CENTRAR ─────────────────────────────────────────────────────
 *
 * Lautaro (19/10/2026): «COMBINAR Y CENTRAR COMO EN EXCEL». El botón de la
 * barra hace lo del de Excel sobre un rango elegido: queda una sola celda con
 * el valor de arriba a la izquierda, centrado en horizontal y en vertical (en
 * el formato de la ancla: `align` y `valign`, como cualquier alineación). Lo
 * que había en las otras celdas se pierde, y si había algo se pregunta antes.
 * Sobre una combinada (elegida entera, o su ancla sola) el mismo botón la
 * separa. Un rango que se cruza con otra combinada sin ser ella no se combina:
 * se avisa cuál pisa, para separarla primero (`queHaceCombinar`).
 *
 * La pregunta de antes de perder algo es de UNA hoja tal como estaba: si la
 * planilla cambia con la pregunta abierta, la pantalla la cierra, y «Combinar
 * igual» igual vuelve a mirar la hoja de ahora (`revisarAntesDeCombinar`):
 * nunca se pierde algo que la pregunta no nombró.
 *
 * ── LO QUE LAS MUEVE ───────────────────────────────────────────────────────
 *
 * Insertar o borrar filas y columnas (`cambiarEstructura` de
 * `shared/planillaOps`) las corre, agranda o achica como a un rango de una
 * fórmula; si queda de una sola celda, deja de ser combinada. Ordenar mueve
 * con su fila las combinadas de una sola fila; las de varias filas en la zona
 * que se ordena se separan (Excel directamente no deja ordenar). Copiar,
 * pegar, rellenar y borrar no las tocan: trabajan con valores.
 */

import { ajustarFormula, colALetra, letraACol, refDe } from "./planilla";

const RE_RANGO = /^\$?([A-Za-z]{1,3})\$?(\d{1,7}):\$?([A-Za-z]{1,3})\$?(\d{1,7})$/;

/* "A1:C3" → { r0, c0, r1, c1 } (índices desde 0), o null. */
export function rangoCombinada(txt) {
  const m = RE_RANGO.exec(String(txt || "").trim());
  if (!m) return null;
  const a = { r: parseInt(m[2], 10) - 1, c: letraACol(m[1]) };
  const b = { r: parseInt(m[4], 10) - 1, c: letraACol(m[3]) };
  return { r0: Math.min(a.r, b.r), r1: Math.max(a.r, b.r), c0: Math.min(a.c, b.c), c1: Math.max(a.c, b.c) };
}

export const textoCombinada = (rg) => `${colALetra(rg.c0)}${rg.r0 + 1}:${colALetra(rg.c1)}${rg.r1 + 1}`;

const seCruzan = (a, b) => a.r0 <= b.r1 && b.r0 <= a.r1 && a.c0 <= b.c1 && b.c0 <= a.c1;

/* La lista limpia: rangos válidos, de más de una celda y sin pisarse (si dos
   se pisan, queda la primera, como haría Excel al abrirlo). `limites`
   opcional = `{ filas, cols }`: lo que se va de la hoja se recorta. Devuelve
   la lista y cuántas se descartaron. */
export function limpiarCombinadas(lista, limites = null) {
  const out = [];
  const tomadas = [];
  let descartadas = 0;
  for (const txt of Array.isArray(lista) ? lista : []) {
    let rg = rangoCombinada(txt);
    if (rg && limites) {
      rg = { ...rg, r1: Math.min(rg.r1, limites.filas - 1), c1: Math.min(rg.c1, limites.cols - 1) };
      if (rg.r0 > rg.r1 || rg.c0 > rg.c1) rg = null;
    }
    if (!rg || (rg.r0 === rg.r1 && rg.c0 === rg.c1)) { if (txt) descartadas += 1; continue; }
    if (tomadas.some((t) => seCruzan(t, rg))) { descartadas += 1; continue; }
    tomadas.push(rg);
    out.push(textoCombinada(rg));
  }
  return { lista: out, descartadas };
}

/* Lo guardado → la lista (siempre un arreglo, aunque venga cualquier cosa). */
export const normalizarCombinadas = (lista) => limpiarCombinadas(lista).lista;

/* `ws["!merges"]` de SheetJS → nuestra lista, y al revés. */
export function combinadasDesdeExcel(merges, limites = null) {
  const lista = (Array.isArray(merges) ? merges : [])
    .filter((m) => m && m.s && m.e)
    .map((m) => textoCombinada({ r0: m.s.r, c0: m.s.c, r1: m.e.r, c1: m.e.c }));
  return limpiarCombinadas(lista, limites);
}
export function combinadasParaExcel(lista) {
  return normalizarCombinadas(lista).map((t) => {
    const rg = rangoCombinada(t);
    return { s: { r: rg.r0, c: rg.c0 }, e: { r: rg.r1, c: rg.c1 } };
  });
}

/* ── Para dibujar y moverse ─────────────────────────────────────────────── */

/* "r,c" → la combinada que cubre esa celda (con su rango). Vacío si no hay
   ninguna: la grilla pregunta por cada celda y tiene que ser barato. */
export function mapaCombinadas(lista) {
  const mapa = new Map();
  for (const txt of normalizarCombinadas(lista)) {
    const rg = rangoCombinada(txt);
    for (let r = rg.r0; r <= rg.r1; r += 1) {
      for (let c = rg.c0; c <= rg.c1; c += 1) mapa.set(`${r},${c}`, rg);
    }
  }
  return mapa;
}

/* Cómo se dibuja la celda (r, c): null si es normal; `{ oculta: true }` si
   la tapa el tramo de su fila; o el tramo: `{ span, cont, tramo, ancla }`,
   con `cont` si es un tramo de abajo (sin valor propio: muestra el fondo de la
   ancla) y `tramo` "solo" | "arriba" | "medio" | "abajo". */
export function celdaCombinada(mapa, r, c) {
  if (!mapa || !mapa.size) return null;
  const rg = mapa.get(`${r},${c}`);
  if (!rg) return null;
  if (c !== rg.c0) return { oculta: true };
  let tramo = "medio";
  if (rg.r0 === rg.r1) tramo = "solo";
  else if (r === rg.r0) tramo = "arriba";
  else if (r === rg.r1) tramo = "abajo";
  return { span: rg.c1 - rg.c0 + 1, cont: r !== rg.r0, tramo, ancla: { r: rg.r0, c: rg.c0 } };
}

/* La ancla de la combinada que cubre (r, c), o null. Ir con el teclado o el
   mouse a una celda tapada lleva a su ancla, como en Excel. */
export function anclaDe(mapa, r, c) {
  const rg = mapa && mapa.size ? mapa.get(`${r},${c}`) : null;
  return rg && (rg.r0 !== r || rg.c0 !== c) ? { r: rg.r0, c: rg.c0 } : null;
}

/* Para moverse con las flechas desde adentro de una combinada: el paso se da
   desde su borde en esa dirección (si no, → desde A1 de «A1:C1» caería en B1,
   que vuelve a la ancla, y no se saldría nunca). */
export function bordeAlMover(mapa, pos, dr, dc) {
  const rg = mapa && mapa.size ? mapa.get(`${pos.r},${pos.c}`) : null;
  if (!rg) return pos;
  return {
    r: dr > 0 ? rg.r1 : dr < 0 ? rg.r0 : pos.r,
    c: dc > 0 ? rg.c1 : dc < 0 ? rg.c0 : pos.c,
  };
}

/* ── Elegir con el mouse ────────────────────────────────────────────────── */

/* Las combinadas de una lista ya como rangos. La lista de una hoja no cambia
   (cada cambio arma otra), así que se guarda: la pantalla lo pide en cada
   dibujo mientras se arrastra. */
const rangosDe = new WeakMap();
function rangosComb(lista) {
  if (!Array.isArray(lista) || !lista.length) return [];
  let out = rangosDe.get(lista);
  if (!out) { out = normalizarCombinadas(lista).map(rangoCombinada); rangosDe.set(lista, out); }
  return out;
}

/* El rango `rg` ({ r0, r1, c0, c1 }) agrandado hasta que las combinadas que
   toca queden enteras adentro. Una que al crecer toca otra, también. */
export function conCombinadasEnteras(rg, combinadas) {
  const rangos = rangosComb(combinadas);
  let out = { r0: rg.r0, r1: rg.r1, c0: rg.c0, c1: rg.c1 };
  for (let crecio = true; crecio;) {
    crecio = false;
    for (const m of rangos) {
      if (m.r0 > out.r1 || m.r1 < out.r0 || m.c0 > out.c1 || m.c1 < out.c0) continue;
      const n = {
        r0: Math.min(out.r0, m.r0), r1: Math.max(out.r1, m.r1),
        c0: Math.min(out.c0, m.c0), c1: Math.max(out.c1, m.c1),
      };
      if (n.r0 !== out.r0 || n.r1 !== out.r1 || n.c0 !== out.c0 || n.c1 !== out.c1) { out = n; crecio = true; }
    }
  }
  return out;
}

/* Lo elegido entre la celda activa `s` y la otra esquina `s2` (null si es
   una celda sola). Cuando la otra esquina se eligió con el mouse —arrastrando
   o con Mayús+clic— lleva `comb: true` y lo elegido abarca enteras las
   combinadas que toca, como en Excel o Sheets: arrastrar del 19 hasta la
   esquina de abajo de un bitren elige los dos renglones (ticket 464). La
   selección que se VE es ésa, y copiar, cortar, borrar o dar formato trabajan
   sobre eso mismo. Con el teclado, o eligiendo columnas o filas enteras (las
   letras y los números), es el rectángulo justo, como antes: elegir la
   columna C no se lleva el título combinado de A1 a E1. */
export function rangoElegido(s, s2, combinadas) {
  const o = s2 || s;
  const rg = {
    r0: Math.min(s.r, o.r), r1: Math.max(s.r, o.r),
    c0: Math.min(s.c, o.c), c1: Math.max(s.c, o.c),
  };
  return s2 && s2.comb ? conCombinadasEnteras(rg, combinadas) : rg;
}

/* Si `rg` es una celda sola o cae entero dentro de una combinada: lo que se
   ve es UN dato. */
export function esUnDato(rg, combinadas) {
  if (rg.r0 === rg.r1 && rg.c0 === rg.c1) return true;
  return rangosComb(combinadas).some((m) => m.r0 <= rg.r0 && m.r1 >= rg.r1 && m.c0 <= rg.c0 && m.c1 >= rg.c1);
}

/* ── Insertar, borrar, ordenar ──────────────────────────────────────────── */

/* Las combinadas después de insertar (`cuantas` > 0) o borrar filas o
   columnas en ESTA hoja (`cambio` como el de `ajustarFormula`). */
export function ajustarCombinadas(lista, cambio) {
  if (!Array.isArray(lista) || !lista.length) return lista;
  const out = [];
  for (const txt of lista) {
    const f = ajustarFormula(`=${txt}`, { ...cambio, hojaFormula: cambio.hoja });
    if (f.includes("#REF")) continue; // se borró entera
    const rg = rangoCombinada(f.slice(1));
    if (rg && (rg.r0 !== rg.r1 || rg.c0 !== rg.c1)) out.push(textoCombinada(rg));
  }
  return out.length === lista.length && out.every((t, i) => t === lista[i]) ? lista : out;
}

/* Las combinadas después de ordenar: `nuevaDe` va de fila vieja a fila nueva
   (las que no están no se movieron). Las de una fila viajan con ella; las de
   varias filas que tocan una fila movida se separan. */
export function ordenarCombinadas(lista, nuevaDe) {
  if (!Array.isArray(lista) || !lista.length) return lista;
  const out = [];
  for (const txt of lista) {
    const rg = rangoCombinada(txt);
    if (!rg) continue;
    const toca = (r) => nuevaDe.has(r) && nuevaDe.get(r) !== r;
    if (rg.r0 === rg.r1) {
      const r = nuevaDe.has(rg.r0) ? nuevaDe.get(rg.r0) : rg.r0;
      out.push(textoCombinada({ ...rg, r0: r, r1: r }));
    } else {
      let movida = false;
      for (let r = rg.r0; r <= rg.r1 && !movida; r += 1) movida = toca(r);
      if (!movida) out.push(txt);
    }
  }
  return out;
}

/* ── Combinar y centrar, separar ────────────────────────────────────────── */

const mismoRango = (a, b) => a.r0 === b.r0 && a.r1 === b.r1 && a.c0 === b.c0 && a.c1 === b.c1;
const esUnaCelda = (rg) => rg.r0 === rg.r1 && rg.c0 === rg.c1;
/* Un rango con al menos una celda: índices enteros, no negativos y no al revés.
   Recortar a la hoja un rango que arranca fuera de ella (una pregunta vieja
   después de borrar filas, una hoja sin celdas) lo deja al revés. */
const tieneCeldas = (rg) => !!rg
  && [rg.r0, rg.c0, rg.r1, rg.c1].every(Number.isInteger)
  && rg.r0 >= 0 && rg.c0 >= 0 && rg.r0 <= rg.r1 && rg.c0 <= rg.c1;

/* Qué hace el botón sobre el rango elegido `rg` (índices desde 0, ya recortado
   a la hoja). Mira sólo las combinadas, no las celdas: la barra lo pregunta en
   cada dibujo.
     · { accion: "separar", rango }  `rg` ES una combinada, o es una celda de
                                    una (la ancla sola, al elegirla): se separa.
     · { accion: "cruce", con }      `rg` pisa combinadas sin ser una de ellas
                                    (`con`, como «A3:A4»): no se combina.
     · { accion: "nada" }            una sola celda suelta, o un rango sin
                                    celdas (al revés o vacío): no hay qué
                                    combinar.
     · { accion: "combinar", rango } */
export function queHaceCombinar(lista, rg) {
  if (!tieneCeldas(rg)) return { accion: "nada" };
  const todas = normalizarCombinadas(lista).map(rangoCombinada);
  if (esUnaCelda(rg)) {
    const la = todas.find((t) => seCruzan(t, rg));
    return la ? { accion: "separar", rango: la } : { accion: "nada" };
  }
  const misma = todas.find((t) => mismoRango(t, rg));
  if (misma) return { accion: "separar", rango: misma };
  const con = todas.filter((t) => seCruzan(t, rg));
  if (con.length) return { accion: "cruce", con: con.map(textoCombinada) };
  return { accion: "combinar", rango: rg };
}

const dentroDeLaHoja = (hoja, rg) => ({
  ...rg,
  r1: Math.min(rg.r1, hoja.celdas.length - 1),
  c1: Math.min(rg.c1, (hoja.celdas[0] || []).length - 1),
});

/* Las celdas de `rg` que no son la ancla y tienen algo (un valor o una nota),
   como «B1»: eso se pierde al combinar, y se pregunta antes, como en Excel. */
export function seBorranAlCombinar(hoja, rg) {
  const tope = dentroDeLaHoja(hoja, rg);
  const formato = hoja.formato || {};
  const out = [];
  for (let r = tope.r0; r <= tope.r1; r += 1) {
    for (let c = tope.c0; c <= tope.c1; c += 1) {
      if (r === tope.r0 && c === tope.c0) continue;
      const v = hoja.celdas[r][c];
      const f = formato[refDe(r, c)];
      if ((v != null && String(v) !== "") || (f && f.nota)) out.push(refDe(r, c));
    }
  }
  return out;
}

/* Combina `rg` y lo centra. La ancla se queda con su valor y su formato, más
   `align: "center"` y `valign: "middle"`; las otras celdas del rango quedan
   vacías y sin formato (en Excel también se pierden: no se ven más). Si no hay
   que combinar —una sola celda, un rango al revés o vacío (también el que
   queda fuera de la hoja al recortarlo), o uno que pisa otra combinada—
   devuelve la misma hoja: `queHaceCombinar` es la que lo decide. */
export function combinarYCentrar(hoja, rg) {
  if (!rg) return hoja;
  const tope = dentroDeLaHoja(hoja, rg);
  if (queHaceCombinar(hoja.combinadas, tope).accion !== "combinar") return hoja;
  const otra = (r, c) => r !== tope.r0 || c !== tope.c0;
  const celdas = hoja.celdas.map((fila, r) => {
    if (r < tope.r0 || r > tope.r1) return fila;
    const n = fila.slice();
    for (let c = tope.c0; c <= tope.c1; c += 1) if (otra(r, c)) n[c] = "";
    return n;
  });
  const formato = { ...(hoja.formato || {}) };
  for (let r = tope.r0; r <= tope.r1; r += 1) {
    for (let c = tope.c0; c <= tope.c1; c += 1) if (otra(r, c)) delete formato[refDe(r, c)];
  }
  const ancla = refDe(tope.r0, tope.c0);
  formato[ancla] = { ...(formato[ancla] || {}), align: "center", valign: "middle" };
  return { ...hoja, celdas, formato, combinadas: [...normalizarCombinadas(hoja.combinadas), textoCombinada(tope)] };
}

const NADA = { accion: "nada" };
const mismasCeldas = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);

/* «Combinar igual». La pregunta (`pedido`) se hizo sobre `pedido.hoja` —la
   hoja misma, el objeto: no su lugar en el libro, que cambia al borrar una— y
   nombró `pedido.seBorran`. Antes de combinar se mira la hoja que está a la
   vista AHORA (`hoja`) y se vuelve a calcular todo sobre ella:
     · { accion: "combinar", rango }   es la misma hoja, sin ningún cambio: se
                                      pierde justo lo que se nombró.
     · { accion: "preguntar", rango, seBorran }  es la misma hoja (por nombre)
                                      pero cambió y algo se perdería: se
                                      pregunta de nuevo, con la lista de ahora.
     · { accion: "cruce", rango, con } ahora pisa otra combinada.
     · { accion: "nada" }              es otra hoja (la de la pregunta se borró
                                      o se cambió), o ya no hay qué combinar ni
                                      qué perder: no se toca nada.
   Nunca combina si la hoja no es la de la pregunta tal cual estaba. */
export function revisarAntesDeCombinar(pedido, hoja) {
  if (!pedido || !pedido.hoja || !pedido.rango || !hoja) return NADA;
  const misma = hoja === pedido.hoja;
  if (!misma && hoja.nombre !== pedido.hoja.nombre) return NADA;
  const tope = dentroDeLaHoja(hoja, pedido.rango);
  const plan = queHaceCombinar(hoja.combinadas, tope);
  if (plan.accion === "cruce") return { accion: "cruce", rango: tope, con: plan.con };
  if (plan.accion !== "combinar") return NADA;
  const seBorran = seBorranAlCombinar(hoja, tope);
  if (misma && mismasCeldas(seBorran, pedido.seBorran || [])) return { accion: "combinar", rango: tope };
  return seBorran.length ? { accion: "preguntar", rango: tope, seBorran } : NADA;
}

/* Separa la combinada `rg`: vuelven a ser celdas sueltas. La ancla se queda con
   su valor y su formato (también el centrado); las demás siguen vacías. */
export function separarCombinada(hoja, rg) {
  const lista = normalizarCombinadas(hoja.combinadas);
  const txt = textoCombinada(rg);
  const quedan = lista.filter((t) => t !== txt);
  return quedan.length === lista.length ? hoja : { ...hoja, combinadas: quedan };
}

/* «B1, C1 y D1»; con muchas, las primeras y «y N celdas más». */
function enumerar(refs, tope, masDe) {
  if (refs.length <= tope) {
    return refs.length === 1 ? refs[0] : `${refs.slice(0, -1).join(", ")} y ${refs[refs.length - 1]}`;
  }
  const resto = refs.length - tope;
  return `${refs.slice(0, tope).join(", ")} y ${resto} ${masDe(resto)}`;
}

/* La pregunta de antes de combinar, cuando se pierde algo. */
export function textoAlCombinar(rg, seBorran) {
  const ancla = refDe(rg.r0, rg.c0);
  const lista = enumerar(seBorran, 6, (n) => (n === 1 ? "celda más" : "celdas más"));
  return `Al combinar ${textoCombinada(rg)} queda sólo lo que hay en ${ancla}, la celda de arriba a la izquierda: se pierde lo de ${lista}.`;
}

/* El aviso de cuando lo elegido se cruza con otras combinadas. */
export function textoCruce(rg, con) {
  const lista = enumerar(con, 3, () => "más");
  const una = con.length === 1;
  return `${textoCombinada(rg)} no se puede combinar: se cruza con ${lista}, que ya ${una ? "está combinada" : "están combinadas"}. `
    + `Para combinar ahí, ${una ? "separala" : "separalas"} primero: ${una ? "elegila" : "elegí cada una"} y tocá «Separar celdas».`;
}
